import React from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { useTheme } from '../../contexts/ThemeContext';
import {
  Plus, Trash, ArrowUp, ArrowDown, GitBranch, ArrowsSplit, Stack,
} from '@phosphor-icons/react';
import {
  newTask, newSplit, newParallel, newSubprocess,
} from '../../lib/workflowSimple';

function updateAt(list, index, updater) {
  return list.map((item, i) => (i === index ? updater(item) : item));
}

function moveItem(list, from, to) {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

function BlockActions({ index, total, onMoveUp, onMoveDown, onRemove, isDark }) {
  return (
    <div className="flex items-center gap-0.5 shrink-0">
      <button type="button" onClick={onMoveUp} disabled={index === 0}
        className={`p-1 rounded disabled:opacity-30 ${isDark ? 'text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800' : 'text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100'}`}>
        <ArrowUp className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={onMoveDown} disabled={index >= total - 1}
        className={`p-1 rounded disabled:opacity-30 ${isDark ? 'text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800' : 'text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100'}`}>
        <ArrowDown className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={onRemove}
        className="p-1 rounded text-red-500 hover:bg-red-500/10">
        <Trash className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function TaskFields({ block, onChange, isDark, compact }) {
  return (
    <div className={`grid gap-2 ${compact ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'}`}>
      <div>
        <label className={`text-[10px] uppercase tracking-wide ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>Step name *</label>
        <Input value={block.label} onChange={(e) => onChange({ ...block, label: e.target.value })}
          placeholder="e.g. Inspect" className={isDark ? 'bg-zinc-900 border-zinc-700 text-zinc-100 h-8 text-sm' : 'h-8 text-sm'} />
      </div>
      <div>
        <label className={`text-[10px] uppercase tracking-wide ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>Status value</label>
        <Input value={block.status_value} onChange={(e) => onChange({ ...block, status_value: e.target.value })}
          placeholder="e.g. inspected" className={isDark ? 'bg-zinc-900 border-zinc-700 text-zinc-100 h-8 text-sm' : 'h-8 text-sm'} />
      </div>
      <div>
        <label className={`text-[10px] uppercase tracking-wide ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>Assignee role</label>
        <Input value={block.assignee_role} onChange={(e) => onChange({ ...block, assignee_role: e.target.value })}
          placeholder="e.g. inspector" className={isDark ? 'bg-zinc-900 border-zinc-700 text-zinc-100 h-8 text-sm' : 'h-8 text-sm'} />
      </div>
      <div className={compact ? 'sm:col-span-2' : 'lg:col-span-1'}>
        <label className={`text-[10px] uppercase tracking-wide ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>Notes</label>
        <Input value={block.description} onChange={(e) => onChange({ ...block, description: e.target.value })}
          placeholder="Optional instructions" className={isDark ? 'bg-zinc-900 border-zinc-700 text-zinc-100 h-8 text-sm' : 'h-8 text-sm'} />
      </div>
    </div>
  );
}

function NestedSteps({ steps, onChange, isDark, depth = 0 }) {
  return (
    <SimpleBlockList blocks={steps} onChange={onChange} isDark={isDark} depth={depth + 1} nested />
  );
}

function SplitBlock({ block, onChange, isDark, depth }) {
  const updateBranch = (idx, branch) => {
    onChange({ ...block, branches: updateAt(block.branches, idx, () => branch) });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className={`text-[10px] uppercase tracking-wide ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>Condition label</label>
        <Input value={block.label} onChange={(e) => onChange({ ...block, label: e.target.value })}
          placeholder="e.g. QC result?" className={isDark ? 'bg-zinc-900 border-zinc-700 text-zinc-100 h-8 text-sm' : 'h-8 text-sm'} />
      </div>
      {(block.branches || []).map((branch, bi) => (
        <div key={branch.id} className={`rounded-lg border p-3 space-y-2 ${isDark ? 'border-amber-500/20 bg-amber-500/5' : 'border-amber-200 bg-amber-50/50'}`}>
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[120px]">
              <label className={`text-[10px] uppercase tracking-wide ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>Branch</label>
              <Input value={branch.label} onChange={(e) => updateBranch(bi, { ...branch, label: e.target.value })}
                className={isDark ? 'bg-zinc-900 border-zinc-700 text-zinc-100 h-8 text-sm' : 'h-8 text-sm'} />
            </div>
            <div className="flex-[2] min-w-[160px]">
              <label className={`text-[10px] uppercase tracking-wide ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>When (condition)</label>
              <Input value={branch.condition} onChange={(e) => updateBranch(bi, { ...branch, condition: e.target.value })}
                placeholder="qc_result == 'pass'" className={isDark ? 'bg-zinc-900 border-zinc-700 text-zinc-100 h-8 text-sm font-mono' : 'h-8 text-sm font-mono'} />
            </div>
            <label className={`flex items-center gap-1.5 text-xs pb-1 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
              <input type="checkbox" checked={Boolean(branch.is_default)}
                onChange={(e) => updateBranch(bi, { ...branch, is_default: e.target.checked })}
                className="rounded border-zinc-600" />
              Default
            </label>
            {(block.branches?.length || 0) > 2 && (
              <Button type="button" size="sm" variant="ghost" className="text-red-500 h-8"
                onClick={() => onChange({ ...block, branches: block.branches.filter((_, i) => i !== bi) })}>
                Remove branch
              </Button>
            )}
          </div>
          <NestedSteps steps={branch.steps || []} onChange={(steps) => updateBranch(bi, { ...branch, steps })} isDark={isDark} depth={depth} />
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange({
        ...block,
        branches: [...(block.branches || []), {
          id: `br-${Date.now()}`,
          label: `Branch ${(block.branches?.length || 0) + 1}`,
          condition: '',
          is_default: false,
          steps: [newTask()],
        }],
      })} className={isDark ? 'border-zinc-700 text-zinc-300' : ''}>
        <Plus className="w-3.5 h-3.5 mr-1" /> Add branch
      </Button>
    </div>
  );
}

function ParallelBlock({ block, onChange, isDark, depth }) {
  const updateLane = (idx, lane) => {
    onChange({ ...block, lanes: updateAt(block.lanes, idx, () => lane) });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className={`text-[10px] uppercase tracking-wide ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>Parallel group label</label>
        <Input value={block.label} onChange={(e) => onChange({ ...block, label: e.target.value })}
          className={isDark ? 'bg-zinc-900 border-zinc-700 text-zinc-100 h-8 text-sm' : 'h-8 text-sm'} />
      </div>
      {(block.lanes || []).map((lane, li) => (
        <div key={lane.id} className={`rounded-lg border p-3 space-y-2 ${isDark ? 'border-violet-500/20 bg-violet-500/5' : 'border-violet-200 bg-violet-50/50'}`}>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className={`text-[10px] uppercase tracking-wide ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>Path name</label>
              <Input value={lane.label} onChange={(e) => updateLane(li, { ...lane, label: e.target.value })}
                className={isDark ? 'bg-zinc-900 border-zinc-700 text-zinc-100 h-8 text-sm' : 'h-8 text-sm'} />
            </div>
            {(block.lanes?.length || 0) > 2 && (
              <Button type="button" size="sm" variant="ghost" className="text-red-500 h-8"
                onClick={() => onChange({ ...block, lanes: block.lanes.filter((_, i) => i !== li) })}>
                Remove path
              </Button>
            )}
          </div>
          <NestedSteps steps={lane.steps || []} onChange={(steps) => updateLane(li, { ...lane, steps })} isDark={isDark} depth={depth} />
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange({
        ...block,
        lanes: [...(block.lanes || []), { id: `lane-${Date.now()}`, label: `Path ${(block.lanes?.length || 0) + 1}`, steps: [newTask()] }],
      })} className={isDark ? 'border-zinc-700 text-zinc-300' : ''}>
        <Plus className="w-3.5 h-3.5 mr-1" /> Add parallel path
      </Button>
    </div>
  );
}

function SubprocessBlock({ block, onChange, isDark, depth }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <label className={`text-[10px] uppercase tracking-wide ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>Sub-process name</label>
          <Input value={block.label} onChange={(e) => onChange({ ...block, label: e.target.value })}
            className={isDark ? 'bg-zinc-900 border-zinc-700 text-zinc-100 h-8 text-sm' : 'h-8 text-sm'} />
        </div>
        <div>
          <label className={`text-[10px] uppercase tracking-wide ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>Template ref (optional)</label>
          <Input value={block.template_id || ''} onChange={(e) => onChange({ ...block, template_id: e.target.value })}
            placeholder="Existing workflow ID" className={isDark ? 'bg-zinc-900 border-zinc-700 text-zinc-100 h-8 text-sm font-mono' : 'h-8 text-sm font-mono'} />
        </div>
      </div>
      <NestedSteps steps={block.steps || []} onChange={(steps) => onChange({ ...block, steps })} isDark={isDark} depth={depth} />
    </div>
  );
}

const TYPE_META = {
  task: { icon: null, label: 'Step', color: 'brand' },
  split: { icon: GitBranch, label: 'Split condition', color: 'amber' },
  parallel: { icon: ArrowsSplit, label: 'Parallel paths', color: 'violet' },
  subprocess: { icon: Stack, label: 'Sub-process', color: 'cyan' },
};

export function SimpleBlockList({ blocks, onChange, isDark, depth = 0, nested = false }) {
  const updateBlock = (idx, next) => onChange(updateAt(blocks, idx, () => next));
  const removeBlock = (idx) => onChange(blocks.filter((_, i) => i !== idx));
  const moveBlock = (idx, dir) => onChange(moveItem(blocks, idx, idx + dir));

  const addBlock = (factory) => onChange([...(blocks || []), factory()]);

  const borderCls = nested
    ? (isDark ? 'border-zinc-800' : 'border-zinc-200')
    : '';

  return (
    <div className={`space-y-2 ${nested ? `pl-3 border-l-2 ${borderCls}` : ''}`}>
      {(blocks || []).length === 0 && (
        <p className={`text-xs py-2 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>No steps yet — add one below.</p>
      )}
      {(blocks || []).map((block, index) => {
        const meta = TYPE_META[block.type] || TYPE_META.task;
        const Icon = meta.icon;
        const headerBg = block.type === 'task'
          ? (isDark ? 'bg-zinc-900/80 border-zinc-800' : 'bg-white border-zinc-200')
          : block.type === 'split'
            ? (isDark ? 'bg-amber-500/10 border-amber-500/20' : 'bg-amber-50 border-amber-200')
            : block.type === 'parallel'
              ? (isDark ? 'bg-violet-500/10 border-violet-500/20' : 'bg-violet-50 border-violet-200')
              : (isDark ? 'bg-cyan-500/10 border-cyan-500/20' : 'bg-cyan-50 border-cyan-200');

        return (
          <div key={block.id} className={`rounded-lg border ${headerBg} overflow-hidden`} data-testid={`workflow-block-${block.type}-${index}`}>
            <div className={`flex items-center gap-2 px-3 py-2 border-b ${isDark ? 'border-zinc-800/80' : 'border-zinc-100'}`}>
              <span className={`text-[10px] font-semibold uppercase tracking-wider shrink-0 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                {depth > 0 ? `${index + 1}.` : `Step ${index + 1}`}
              </span>
              {Icon && <Icon className={`w-3.5 h-3.5 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`} />}
              {block.type !== 'task' && (
                <span className={`text-xs font-medium ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>{meta.label}</span>
              )}
              <div className="ml-auto">
                <BlockActions index={index} total={blocks.length}
                  onMoveUp={() => moveBlock(index, -1)}
                  onMoveDown={() => moveBlock(index, 1)}
                  onRemove={() => removeBlock(index)}
                  isDark={isDark} />
              </div>
            </div>
            <div className="p-3">
              {block.type === 'task' && (
                <TaskFields block={block} onChange={(next) => updateBlock(index, next)} isDark={isDark} compact={nested} />
              )}
              {block.type === 'split' && (
                <SplitBlock block={block} onChange={(next) => updateBlock(index, next)} isDark={isDark} depth={depth} />
              )}
              {block.type === 'parallel' && (
                <ParallelBlock block={block} onChange={(next) => updateBlock(index, next)} isDark={isDark} depth={depth} />
              )}
              {block.type === 'subprocess' && (
                <SubprocessBlock block={block} onChange={(next) => updateBlock(index, next)} isDark={isDark} depth={depth} />
              )}
            </div>
          </div>
        );
      })}

      <div className="flex flex-wrap gap-2 pt-1">
        <Button type="button" size="sm" variant="outline" onClick={() => addBlock(() => newTask())}
          className={isDark ? 'border-zinc-700 text-zinc-300' : ''} data-testid="add-workflow-step">
          <Plus className="w-3.5 h-3.5 mr-1" /> Step
        </Button>
        {!nested && (
          <>
            <Button type="button" size="sm" variant="outline" onClick={() => addBlock(() => newSplit())}
              className={isDark ? 'border-zinc-700 text-zinc-300' : ''} data-testid="add-workflow-split">
              <GitBranch className="w-3.5 h-3.5 mr-1" /> Split
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => addBlock(() => newParallel())}
              className={isDark ? 'border-zinc-700 text-zinc-300' : ''} data-testid="add-workflow-parallel">
              <ArrowsSplit className="w-3.5 h-3.5 mr-1" /> Parallel
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => addBlock(() => newSubprocess())}
              className={isDark ? 'border-zinc-700 text-zinc-300' : ''} data-testid="add-workflow-subprocess">
              <Stack className="w-3.5 h-3.5 mr-1" /> Sub-process
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default SimpleBlockList;
